import React, { useState } from "react";
import { Capability, Card, Empty, ErrorState, Kpi, PageHeading, Rate, RowsSkeleton, Table } from "../ui";
import { BarList, Meter, SegmentBar } from "../charts";
import { useFilters } from "../filters";
import { capabilityLabel, useView } from "../data";
import { fmt, ms, pct, statusOf, words } from "../format";

/** Every capability measured the same way: started, finished, asked, waited, failed, stopped — and its own stages. */
export default function Capabilities() {
  const { params } = useFilters();
  const { data, loading, error } = useView("capabilities", params);
  const [open, setOpen] = useState(null);
  const rows = data?.capabilities ?? [];
  const started = rows.reduce((n, r) => n + r.started, 0);
  const finished = rows.reduce((n, r) => n + Math.min(r.finished, r.started), 0);
  const failed = rows.reduce((n, r) => n + Math.min(r.failed, r.started), 0);
  const selected = rows.find((r) => r.capability === open);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="Capabilities">
        Each capability's steps, measured the same way. Click a row to see the stages it records.
      </PageHeading>
      <div className="aa-grid aa-grid-kpi">
        <Kpi loading={loading} label="Capabilities used" value={fmt(rows.filter((r) => r.started).length)} />
        <Kpi loading={loading} label="Steps started" info="One step is one piece of work a capability did for a request." value={fmt(started)} />
        <Kpi loading={loading} label="Finished" value={started ? pct(finished / started) : "—"} status={started ? statusOf(finished / started, { good: 0.8, bad: 0.5 }) : "none"} foot={`${fmt(finished)} of ${fmt(started)} steps`} />
        <Kpi loading={loading} label="Failed" value={started ? pct(failed / started) : "—"} status={started ? statusOf(failed / started, { good: 0.02, bad: 0.1, higherIsBetter: false }) : "none"} foot={`${fmt(failed)} steps`} />
      </div>

      <Card title="By capability" subtitle="How each capability's steps ended" flush>
        {loading ? (
          <RowsSkeleton />
        ) : error ? (
          <ErrorState error={error} />
        ) : rows.length ? (
          <Table
            defaultSort={{ key: "started", dir: "desc" }}
            onRow={(r) => setOpen(open === r.capability ? null : r.capability)}
            columns={[
              { key: "capability", label: "Capability", render: (r) => <Capability id={r.capability} />, sort: (r) => capabilityLabel(r.capability) },
              { key: "started", label: "Started", align: "right", render: (r) => fmt(r.started) },
              {
                key: "outcome",
                label: "How steps ended",
                sortable: false,
                width: 200,
                render: (r) => (
                  <SegmentBar
                    segments={[
                      { label: "Finished", value: r.finished, color: "var(--aa-good)" },
                      { label: "Asked the user", value: r.asked, color: "var(--aa-s1)" },
                      { label: "Waited on a job", value: r.waiting_job, color: "var(--aa-neutral)" },
                      { label: "Stopped", value: r.stopped, color: "var(--aa-warn)" },
                      { label: "Failed", value: r.failed, color: "var(--aa-bad)" },
                    ]}
                  />
                ),
              },
              { key: "completionRate", label: "Finished", align: "right", render: (r) => <Rate value={r.completionRate} status={statusOf(r.completionRate, { good: 0.8, bad: 0.5 })} /> },
              { key: "asked", label: "Asked", align: "right", render: (r) => fmt(r.asked) },
              { key: "failed", label: "Failed", align: "right", render: (r) => <span style={{ color: r.failed ? "var(--aa-bad-ink)" : "var(--aa-faint)", fontWeight: r.failed ? 600 : 400 }}>{fmt(r.failed)}</span> },
              { key: "stopped", label: "Stopped", align: "right", render: (r) => fmt(r.stopped) },
              { key: "avgWorkMs", label: "Avg work", align: "right", render: (r) => <Rate value={r.avgWorkMs} format={ms} status={statusOf(r.avgWorkMs, { good: 10_000, bad: 30_000, higherIsBetter: false })} /> },
              { key: "credits", label: "Credits", align: "right", render: (r) => fmt(r.credits) },
              { key: "friction", label: "Stuck", align: "right", render: (r) => <span style={{ color: r.friction ? "var(--aa-warn-ink)" : "var(--aa-faint)" }}>{fmt(r.friction)}</span> },
            ]}
            rows={rows.map((r) => ({ ...r, id: r.capability }))}
          />
        ) : (
          <Empty icon="capabilities" title="No capability has run yet">
            Rows appear as soon as the agent does work in this period.
          </Empty>
        )}
        {rows.length > 0 && (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", padding: "12px 20px", borderTop: "1px solid var(--aa-border)", fontSize: 12, color: "var(--aa-muted)" }}>
            {[
              ["Finished", "var(--aa-good)"],
              ["Asked the user", "var(--aa-s1)"],
              ["Waited on a job", "var(--aa-neutral)"],
              ["Stopped", "var(--aa-warn)"],
              ["Failed", "var(--aa-bad)"],
            ].map(([l, c]) => (
              <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span className="aa-swatch" style={{ background: c }} />
                {l}
              </span>
            ))}
          </div>
        )}
      </Card>

      {selected && (
        <Card
          title={`Stages — ${capabilityLabel(selected.capability)}`}
          subtitle="Milestones this capability records, most reached first. The share is of its steps started."
          actions={
            <button type="button" className="aa-btn aa-btn-sm" onClick={() => setOpen(null)}>
              Close
            </button>
          }
        >
          {selected.stages.length ? (
            <BarList
              rows={selected.stages.map((s) => ({
                key: s.stage,
                label: (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    {words(s.stage)}
                    <Meter value={selected.started ? s.count / selected.started : 0} width={48} />
                  </span>
                ),
                value: s.count,
                note: selected.started ? pct(s.count / selected.started) : "",
              }))}
            />
          ) : (
            <Empty compact title="No stages of its own">
              Its steps above are the whole picture.
            </Empty>
          )}
        </Card>
      )}
    </div>
  );
}
